import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    apiReference: {
      insertApiReference: (attrs?: {
        method?: ApiReferenceMethod;
        url?: string;
      }) => ReturnType;
      updateApiReference: (attrs: {
        method?: ApiReferenceMethod;
        url?: string;
      }) => ReturnType;
    };
  }
}

export type ApiReferenceMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export interface ApiReferenceOptions {
  HTMLAttributes: Record<string, any>;
  view: any;
}

const ALLOWED_METHODS: ApiReferenceMethod[] = [
  'get',
  'post',
  'put',
  'patch',
  'delete',
];

function normalizeMethod(v: unknown): ApiReferenceMethod {
  if (typeof v !== 'string') return 'get';
  const lower = v.toLowerCase() as ApiReferenceMethod;
  return ALLOWED_METHODS.includes(lower) ? lower : 'get';
}

export const ApiReference = Node.create<ApiReferenceOptions>({
  name: 'apiReference',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      view: null,
    };
  },

  addAttributes() {
    return {
      method: {
        default: 'get' as ApiReferenceMethod,
        parseHTML: (el) => normalizeMethod(el.getAttribute('data-method')),
        renderHTML: (attrs) => ({
          'data-method': normalizeMethod(attrs.method),
        }),
      },
      url: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-url') ?? '',
        renderHTML: (attrs) => ({
          'data-url': typeof attrs.url === 'string' ? attrs.url : '',
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="api-reference"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes,
      ),
    ];
  },

  addCommands() {
    return {
      insertApiReference:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              method: normalizeMethod(attrs?.method),
              url: typeof attrs?.url === 'string' ? attrs.url : '',
            },
          }),
      updateApiReference:
        (attrs) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, {
            ...(attrs.method !== undefined && {
              method: normalizeMethod(attrs.method),
            }),
            ...(attrs.url !== undefined && { url: attrs.url }),
          }),
    };
  },

  addNodeView() {
    if (!this.options.view) return null;
    return ReactNodeViewRenderer(this.options.view);
  },
});
